import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PageContainer, PageCard, PageHeader, PageHeaderContent, PageHeaderTitle, PageHeaderHeading, PageHeaderDescription, PageHeaderActions, PageContent } from '@/components/ui/page'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { EditButton, DeleteButton } from '@/components/ui/action-buttons'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, Calendar, Clock, Settings } from 'lucide-react'
import { WorkingHoursManager } from '@/components/calendar/WorkingHoursManager'
import { useCalendars } from '@/hooks/useCalendars'
import { useCalendarMutations } from '@/hooks/useCalendarMutations'
import type { CalendarCreateDTO, CalendarUpdateDTO, LeadTimeUnit } from '@/api/generated/models'
import type { Calendar as CalendarType } from '@/hooks/useCalendars'

export const Route = createFileRoute('/calendar-configuration')({
  component: CalendarConfiguration,
  errorComponent: RouteErrorFallback,
})

// Common timezones
const timezones = [
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Vienna',
  'Europe/Zurich',
  'UTC'
]

// Lead time unit options with German labels
const LEAD_TIME_UNITS = [
  { value: 'MINUTES' as LeadTimeUnit, label: 'Minuten' },
  { value: 'HOURS' as LeadTimeUnit, label: 'Stunden' },
  { value: 'DAYS' as LeadTimeUnit, label: 'Tage' },
  { value: 'WEEKS' as LeadTimeUnit, label: 'Wochen' },
  { value: 'MONTHS' as LeadTimeUnit, label: 'Monate' }
] as const

const getLeadTimeUnitLabel = (unit: LeadTimeUnit | string): string => {
  const found = LEAD_TIME_UNITS.find(u => u.value === unit)
  return found?.label ?? unit
}

type DialogMode = 'view' | 'edit'

function CalendarConfiguration() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [selectedCalendar, setSelectedCalendar] = useState<CalendarType | null>(null)
  const [dialogMode, setDialogMode] = useState<DialogMode>('view')
  
  const { data: calendars = [], isLoading, error } = useCalendars()
  const { createCalendar, updateCalendar, deleteCalendar } = useCalendarMutations()
  
  const [createFormData, setCreateFormData] = useState<CalendarCreateDTO>({
    name: '',
    description: '',
    timezone: 'Europe/Berlin',
    is_active: true,
    location_id: undefined,
    lead_time_value: undefined,
    lead_time_unit: undefined
  })

  const [editFormData, setEditFormData] = useState<CalendarUpdateDTO>({
    name: '',
    description: '',
    timezone: 'Europe/Berlin',
    is_active: true,
    location_id: undefined,
    lead_time_value: undefined,
    lead_time_unit: undefined
  })

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await createCalendar.mutateAsync(createFormData)
      setCreateDialogOpen(false)
      setCreateFormData({
        name: '',
        description: '',
        timezone: 'Europe/Berlin',
        is_active: true,
        location_id: undefined,
        lead_time_value: undefined,
        lead_time_unit: undefined
      })
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  }

  const handleCardClick = (calendar: CalendarType) => {
    setSelectedCalendar(calendar)
    setDialogMode('view')
    setDetailDialogOpen(true)
  }

  const handleEditClick = () => {
    if (selectedCalendar) {
      setEditFormData({
        name: selectedCalendar.name,
        description: selectedCalendar.description,
        timezone: selectedCalendar.timezone,
        is_active: selectedCalendar.is_active,
        location_id: selectedCalendar.location_id,
        lead_time_value: selectedCalendar.lead_time_value,
        lead_time_unit: selectedCalendar.lead_time_unit
      })
      setDialogMode('edit')
    }
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedCalendar) return

    try {
      await updateCalendar.mutateAsync({ id: selectedCalendar.id, data: editFormData })
      setDialogMode('view')
      // Refresh selected calendar data
      const updatedCalendar = {
        ...selectedCalendar,
        ...editFormData,
        name: editFormData.name || selectedCalendar.name,
        is_active: editFormData.is_active ?? selectedCalendar.is_active,
        lead_time_value: editFormData.lead_time_value,
        lead_time_unit: editFormData.lead_time_unit
      }
      setSelectedCalendar(updatedCalendar as CalendarType)
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  }

  const handleDeleteCalendar = async () => {
    if (!selectedCalendar) return
    
    await deleteCalendar.mutateAsync(selectedCalendar.id)
    setDetailDialogOpen(false)
    setSelectedCalendar(null)
    setDialogMode('view')
  }

  const closeDetailDialog = () => {
    setDetailDialogOpen(false)
    setSelectedCalendar(null)
    setDialogMode('view')
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="text-center text-red-600">
          <p>Fehler beim Laden der Kalender. Bitte versuchen Sie es erneut.</p>
        </div>
      </div>
    )
  }

  return (
    <PageContainer padding="p-4 sm:p-8">
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <PageHeaderHeading>Kalender</PageHeaderHeading>
              <PageHeaderDescription>Verwalten Sie Ihre Kalender und Terminzeiten</PageHeaderDescription>
            </PageHeaderTitle>
            <PageHeaderActions>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full sm:w-auto">
                    <Plus className="mr-2 h-4 w-4" />
                    Kalender hinzufügen
                  </Button>
                </DialogTrigger>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-[500px]">
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <DialogHeader>
                <DialogTitle>Neuen Kalender hinzufügen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={createFormData.name}
                    onChange={(e) => setCreateFormData({...createFormData, name: e.target.value})}
                    required
                    placeholder="z.B. Max Mustermann"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    value={createFormData.description || ''}
                    onChange={(e) => setCreateFormData({...createFormData, description: e.target.value})}
                    rows={3}
                    placeholder="Optional: Beschreibung des Kalenders"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Zeitzone</Label>
                  <Select value={createFormData.timezone} onValueChange={(value) => setCreateFormData({...createFormData, timezone: value})}>
                    <SelectTrigger id="timezone" className="w-full">
                      <SelectValue placeholder="Wählen Sie eine Zeitzone" />
                    </SelectTrigger>
                    <SelectContent>
                      {timezones.map(timezone => (
                        <SelectItem key={timezone} value={timezone}>{timezone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={createFormData.is_active}
                    onCheckedChange={(checked) => setCreateFormData({...createFormData, is_active: checked})}
                  />
                  <Label htmlFor="active">Aktiv</Label>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="lead-time-enabled"
                      checked={createFormData.lead_time_value != null}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setCreateFormData({...createFormData, lead_time_value: 1, lead_time_unit: 'DAYS'})
                        } else {
                          setCreateFormData({...createFormData, lead_time_value: undefined, lead_time_unit: undefined})
                        }
                      }}
                    />
                    <Label htmlFor="lead-time-enabled">Vorlaufzeit</Label>
                  </div>
                  {createFormData.lead_time_value != null && (
                    <div className="flex gap-2 pl-6">
                      <Input
                        id="lead-time-value"
                        type="number"
                        min="1"
                        value={createFormData.lead_time_value}
                        onChange={(e) => setCreateFormData({...createFormData, lead_time_value: parseInt(e.target.value) || 1})}
                        className="w-24"
                      />
                      <Select
                        value={createFormData.lead_time_unit || 'DAYS'}
                        onValueChange={(value) => setCreateFormData({...createFormData, lead_time_unit: value as LeadTimeUnit})}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_TIME_UNITS.map(unit => (
                            <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setCreateDialogOpen(false)}
                  disabled={createCalendar.isPending}
                >
                  Abbrechen
                </Button>
                <Button 
                  type="submit" 
                  disabled={createCalendar.isPending}
                >
                  {createCalendar.isPending ? 'Erstelle...' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
            </PageHeaderActions>
          </PageHeaderContent>
        </PageHeader>

        <PageContent className="p-6 overflow-y-auto">
      {/* Calendar Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={closeDetailDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-[800px] max-h-[90dvh] overflow-y-auto">
          {selectedCalendar && (
            <>
              {dialogMode === 'view' && (
                <>
                  <DialogHeader>
                    <DialogTitle>Kalenderdetails</DialogTitle>
                  </DialogHeader>
                  
                  <Tabs defaultValue="details" className="pt-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="details" className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        Details
                      </TabsTrigger>
                      <TabsTrigger value="working-hours" className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Terminzeiten
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="details" className="space-y-4 mt-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-semibold">{selectedCalendar.name}</h4>
                        <Badge variant={selectedCalendar.is_active ? 'default' : 'secondary'}>
                          {selectedCalendar.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </div>

                      {selectedCalendar.description && (
                        <div>
                          <h4 className="text-sm font-medium text-muted-foreground mb-1">Beschreibung</h4>
                          <p className="text-base">{selectedCalendar.description}</p>
                        </div>
                      )}

                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Zeitzone</h4>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <p className="text-base">{selectedCalendar.timezone}</p>
                        </div>
                      </div>

                      {selectedCalendar.location_id && (
                        <div>
                          <h4 className="text-sm font-medium text-muted-foreground mb-1">Standort-ID</h4>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <p className="text-base">{selectedCalendar.location_id}</p>
                          </div>
                        </div>
                      )}

                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Vorlaufzeit</h4>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <p className="text-base">
                            {selectedCalendar.lead_time_value != null && selectedCalendar.lead_time_unit
                              ? `${selectedCalendar.lead_time_value} ${getLeadTimeUnitLabel(selectedCalendar.lead_time_unit)}`
                              : 'Keine Vorlaufzeit konfiguriert'}
                          </p>
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="working-hours" className="mt-4">
                      <WorkingHoursManager calendarId={selectedCalendar.id} />
                    </TabsContent>
                  </Tabs>

                  <DialogFooter className="gap-2 pt-4">
                    <EditButton onClick={handleEditClick} />
                    <DeleteButton 
                      onDelete={handleDeleteCalendar}
                      itemName={selectedCalendar.name}
                      confirmationTitle="Kalender löschen?"
                      confirmationDescription={`Sind Sie sicher, dass Sie den Kalender "${selectedCalendar.name}" löschen möchten? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
                    />
                  </DialogFooter>
                </>
              )}

              {dialogMode === 'edit' && (
                <>
                  <DialogHeader>
                    <DialogTitle>Kalender bearbeiten</DialogTitle>
                  </DialogHeader>
                  
                  <Tabs defaultValue="details" className="pt-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="details" className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        Details
                      </TabsTrigger>
                      <TabsTrigger value="working-hours" className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Terminzeiten
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="details" className="mt-4">
                      <form onSubmit={handleEditSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit-name">Name</Label>
                          <Input
                            id="edit-name"
                            value={editFormData.name || ''}
                            onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-description">Beschreibung</Label>
                          <Textarea
                            id="edit-description"
                            value={editFormData.description || ''}
                            onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                            placeholder="Optional: Beschreibung des Kalenders"
                            rows={3}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-timezone">Zeitzone</Label>
                          <Select value={editFormData.timezone || ''} onValueChange={(value) => setEditFormData({...editFormData, timezone: value})}>
                            <SelectTrigger id="edit-timezone" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {timezones.map(timezone => (
                                <SelectItem key={timezone} value={timezone}>{timezone}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="edit-active"
                            checked={editFormData.is_active ?? false}
                            onCheckedChange={(checked) => setEditFormData({...editFormData, is_active: checked})}
                          />
                          <Label htmlFor="edit-active">Aktiv</Label>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id="edit-lead-time-enabled"
                              checked={editFormData.lead_time_value != null}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setEditFormData({...editFormData, lead_time_value: 1, lead_time_unit: 'DAYS'})
                                } else {
                                  setEditFormData({...editFormData, lead_time_value: null, lead_time_unit: null})
                                }
                              }}
                            />
                            <Label htmlFor="edit-lead-time-enabled">Vorlaufzeit</Label>
                          </div>
                          {editFormData.lead_time_value != null && (
                            <div className="flex gap-2 pl-6">
                              <Input
                                id="edit-lead-time-value"
                                type="number"
                                min="1"
                                value={editFormData.lead_time_value}
                                onChange={(e) => setEditFormData({...editFormData, lead_time_value: parseInt(e.target.value) || 1})}
                                className="w-24"
                              />
                              <Select
                                value={editFormData.lead_time_unit || 'DAYS'}
                                onValueChange={(value) => setEditFormData({...editFormData, lead_time_unit: value as LeadTimeUnit})}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {LEAD_TIME_UNITS.map(unit => (
                                    <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </form>
                    </TabsContent>

                    <TabsContent value="working-hours" className="mt-4">
                      <WorkingHoursManager calendarId={selectedCalendar.id} />
                    </TabsContent>
                  </Tabs>
                  
                  <DialogFooter className="gap-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setDialogMode('view')}
                      disabled={updateCalendar.isPending}
                    >
                      Abbrechen
                    </Button>
                    <Button 
                      type="button" 
                      onClick={handleEditSubmit}
                      disabled={updateCalendar.isPending}
                    >
                      {updateCalendar.isPending ? 'Speichere...' : 'Speichern'}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {calendars.map(calendar => (
          <Card 
            key={calendar.id} 
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => handleCardClick(calendar)}
          >
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-xl">{calendar.name}</CardTitle>
                <Badge variant={calendar.is_active ? 'default' : 'secondary'}>
                  {calendar.is_active ? 'Aktiv' : 'Inaktiv'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {calendar.description && (
                <div>
                  <p className="text-sm text-muted-foreground">{calendar.description}</p>
                </div>
              )}

              <div>
                <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>Zeitzone</span>
                </div>
                <Badge variant="outline">
                  {calendar.timezone}
                </Badge>
              </div>

              {calendar.location_id && (
                <div>
                  <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>Standort-ID</span>
                  </div>
                  <Badge variant="outline">
                    {calendar.location_id}
                  </Badge>
                </div>
              )}

              {calendar.lead_time_value != null && calendar.lead_time_unit && (
                <div>
                  <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Vorlaufzeit</span>
                  </div>
                  <Badge variant="outline">
                    {calendar.lead_time_value} {getLeadTimeUnitLabel(calendar.lead_time_unit)}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        
        {calendars.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">Keine Kalender vorhanden</p>
            <p>Fügen Sie Ihren ersten Kalender hinzu, um zu beginnen.</p>
          </div>
        )}
      </div>
        </PageContent>
      </PageCard>
    </PageContainer>
  )
}